import type {
  ProductEditorForm,
  ProductEditorSyncValidator,
  ProductEditorAsyncValidator,
} from '@/components/forms/ProductForm.types'
import type { ProductRole } from '@/lib/product-role'
import type { ProductEditorCollection } from '@/lib/services/products/products.form'
import { useStore } from '@tanstack/react-form'
import { Camera } from 'lucide-react'
import { ProductAddOnStorefrontVisibility } from '@/lib/enums/ProductAddOnStorefrontVisibility'
import { collectionsHaveVisibleCollection } from '@/lib/services/products/products.visibility'
import { formatCurrency, normalizeCurrencyValue } from '@/lib/utils'
import ImageThumbnail from '@/components/media/ImageThumbnail'
import { DetailCard, DetailWell } from '@/components/patterns/detail-page'
import { MediaPlaceholder } from '@/components/patterns/media-placeholder'
import { ActionRow } from '@/components/patterns/action-row'
import { Badge } from '@/components/ui/badge'
import { Typography } from '@/components/ui/typography'
import ProductStorefrontVisibilityBadge from './ProductStorefrontVisibilityBadge'
import { getProductSummaryVisibilityMeta, ProductVisibilityPicker } from './ProductVisibilityPicker'

type SummarySectionName = 'photos' | 'variants' | 'collections' | 'appliesTo'

export function ProductSummarySidebar<
  TOnMount extends ProductEditorSyncValidator,
  TOnChange extends ProductEditorSyncValidator,
  TOnChangeAsync extends ProductEditorAsyncValidator,
  TOnBlur extends ProductEditorSyncValidator,
  TOnBlurAsync extends ProductEditorAsyncValidator,
  TOnSubmit extends ProductEditorSyncValidator,
  TOnSubmitAsync extends ProductEditorAsyncValidator,
  TOnDynamic extends ProductEditorSyncValidator,
  TOnDynamicAsync extends ProductEditorAsyncValidator,
  TOnServer extends ProductEditorAsyncValidator,
  TSubmitMeta,
>({
  form,
  id,
  initialCollections = [],
  isAddOnProduct = false,
  isSoldStandalone = true,
  isSubscriptionPackage = false,
  previewSource,
  productRole = 'product',
  sectionIds,
  onJumpToSection,
  canViewCollections,
}: {
  form: ProductEditorForm<
    TOnMount,
    TOnChange,
    TOnChangeAsync,
    TOnBlur,
    TOnBlurAsync,
    TOnSubmit,
    TOnSubmitAsync,
    TOnDynamic,
    TOnDynamicAsync,
    TOnServer,
    TSubmitMeta
  >
  id: string
  initialCollections?: ProductEditorCollection[]
  isAddOnProduct?: boolean
  isSoldStandalone?: boolean
  isSubscriptionPackage?: boolean
  previewSource?: { name?: string | null } | null
  productRole?: ProductRole
  sectionIds?: Partial<Record<SummarySectionName, string>>
  onJumpToSection?: (sectionName: SummarySectionName) => void
  canViewCollections?: boolean
}) {
  const name =
    previewSource?.name ||
    (isSubscriptionPackage
      ? 'Untitled subscription package'
      : isAddOnProduct
        ? 'Untitled add-on'
        : 'Untitled product')
  const storefrontVisibility = useStore(form.store, (state) => state.values.storefront_visibility)
  const addOnStorefrontVisibility = useStore(
    form.store,
    (state) =>
      state.values.add_on_storefront_visibility ?? ProductAddOnStorefrontVisibility.Hidden.Value
  )
  const collections = useStore(form.store, (state) => state.values.collections ?? [])
  const supportsCollections = isSoldStandalone || isSubscriptionPackage
  const standaloneCollections = supportsCollections ? collections : []
  const collectionCount = standaloneCollections.length
  const visibleSmartCollections = supportsCollections
    ? initialCollections.filter((collection) => collection?.collection_membership_type === 'smart')
    : []
  const hasVisibleCollection =
    collectionsHaveVisibleCollection(standaloneCollections) ||
    collectionsHaveVisibleCollection(visibleSmartCollections)
  const addOnTargetCount = useStore(form.store, (state) => state.values.add_on_sets?.length ?? 0)
  const mediaCount = useStore(form.store, (state) => state.values.media?.length ?? 0)
  const primaryMedia = useStore(form.store, (state) => state.values.media?.[0] ?? null)
  const variantCount = useStore(form.store, (state) => state.values.variants?.length ?? 0)
  const startingPrice = useStore(form.store, (state) => {
    const variants = state.values.variants ?? []

    return variants.length > 0
      ? Math.min(...variants.map((variant) => normalizeCurrencyValue(variant?.price ?? 0)))
      : null
  })
  const visibilityMeta = getProductSummaryVisibilityMeta({
    addOnVisibility: addOnStorefrontVisibility,
    hasVisibleCollection,
    isAddOnProduct,
    isSoldStandalone,
    isSubscriptionPackage,
    storefrontVisibility,
  })
  const summaryStats = (
    [
      {
        label: 'Photos',
        value: mediaCount,
        sectionName: 'photos',
        sectionId: sectionIds?.photos,
      },
      {
        label: isSubscriptionPackage ? 'Tiers' : 'Variants',
        value: variantCount,
        sectionName: 'variants',
        sectionId: sectionIds?.variants,
      },
      supportsCollections
        ? {
            label: 'Collections',
            value: collectionCount,
            sectionName: 'collections',
            sectionId: canViewCollections ? sectionIds?.collections : null,
          }
        : null,
      isAddOnProduct
        ? {
            label: 'Offered with',
            value: addOnTargetCount,
            sectionName: 'appliesTo',
            sectionId: sectionIds?.appliesTo,
          }
        : null,
    ] satisfies ({
      label: string
      value: number
      sectionName: SummarySectionName
      sectionId: string | null | undefined
    } | null)[]
  ).filter((stat): stat is NonNullable<typeof stat> => Boolean(stat))

  return (
    <DetailCard accent={visibilityMeta.accent} warning={visibilityMeta.warning}>
      <div className="space-y-5">
        <div className="space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <Typography variant="eyebrow">Summary</Typography>
            <ProductStorefrontVisibilityBadge
              product={{
                storefront_visibility: storefrontVisibility,
                add_on_storefront_visibility: addOnStorefrontVisibility,
                is_add_on: isAddOnProduct,
                is_add_on_only: isAddOnProduct && !isSoldStandalone,
              }}
              hasVisibleCollection={isSubscriptionPackage || hasVisibleCollection}
            />
          </div>

          <div className="flex items-start gap-3">
            {primaryMedia ? (
              <ImageThumbnail
                media={primaryMedia}
                className="w-20 rounded-xl border border-border/60 shadow-none"
              />
            ) : (
              <MediaPlaceholder icon={Camera} size="md" />
            )}

            <div className="min-w-0 space-y-2">
              <Typography variant="h3" wrap="balance" className="pb-0">
                {name}
              </Typography>
              <div className="flex flex-wrap gap-2">
                {startingPrice !== null ? (
                  <Badge variant="brand">Starting at {formatCurrency(startingPrice)}</Badge>
                ) : null}
              </div>
            </div>
          </div>
        </div>

        <div className="overflow-hidden rounded-xl border border-border/60 bg-muted/[0.04]">
          {summaryStats.map((stat, index) => (
            <ActionRow
              padding="none"
              radius="none"
              key={stat.label}
              disabled={!stat.sectionId}
              onClick={() => onJumpToSection?.(stat.sectionName)}
              className={[
                'flex w-full min-w-0 items-center justify-between gap-4 px-4 py-3 text-left transition-colors',
                index > 0 ? 'border-t border-border/60' : '',
              ].join(' ')}
            >
              <Typography variant="eyebrow">{stat.label}</Typography>
              <Typography variant="h5" align="right" className="pb-0 tabular-nums">
                {stat.value}
              </Typography>
            </ActionRow>
          ))}
        </div>

        <DetailWell className="space-y-2">
          <ProductVisibilityPicker
            form={form}
            id={`${id}-storefront-visibility`}
            label="Visible on website"
            productRole={productRole}
          />
        </DetailWell>
      </div>
    </DetailCard>
  )
}
